
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchShippingSettings, getCachedShippingSettings } from "../utils/shippingConfig";

const BANNER_API =
  "https://api.vanyacollectionsrajahmundry.com/banner/all";

const DEFAULT_BANNER = {
  image_url: "/herosection.jpg",
  description:
    "Discover handcrafted silk and designer sarees from India's master weavers. Each piece tells a story of tradition, artistry, and timeless beauty.",
};

const HeroSection = () => {
  const [banner, setBanner] = useState(null);
  const [isLoadingBanner, setIsLoadingBanner] = useState(true);
  const [shippingSettings, setShippingSettings] = useState(getCachedShippingSettings);

  const formattedThreshold = Number(
    shippingSettings?.free_shipping_threshold ?? 5000
  ).toLocaleString("en-IN");

  const tickerItems = [
    "BANARASI SILK",
    "KANJEEVARAM",
    "PURE HANDLOOM",
    "BRIDAL COLLECTION",
    "DESIGNER SAREES",
    `FREE SHIPPING OVER ₹${formattedThreshold}`,
  ];

  // =====================================================
  // FETCH BANNER FROM BACKEND
  // =====================================================

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, 4500); // 4.5s max wait before auto-falling back to default

    const fetchBanner = async () => {
      try {
        const response = await fetch(BANNER_API, { signal: controller.signal });

        if (!response.ok) {
          throw new Error("Failed to fetch banner");
        }

        const data = await response.json();
        console.log("Hero banner:", data);

        if (data?.image_url) {
          const img = new Image();
          img.src = data.image_url;
          img.onload = () => {
            if (isMounted) {
              setBanner(data);
              setIsLoadingBanner(false);
            }
          };
          img.onerror = () => {
            console.warn("Banner image failed to load, falling back to default");
            if (isMounted) {
              setBanner(DEFAULT_BANNER);
              setIsLoadingBanner(false);
            }
          };
        } else {
          if (isMounted) {
            setBanner(data);
            setIsLoadingBanner(false);
          }
        }
      } catch (error) {
        console.warn("Hero banner fetch/timeout error, falling back to default:", error.message);
        if (isMounted) {
          setBanner(DEFAULT_BANNER);
          setIsLoadingBanner(false);
        }
      } finally {
        clearTimeout(timeoutId);
      }
    };

    fetchBanner();

    // Load dynamic shipping rules
    const loadShipping = async () => {
      try {
        const settings = await fetchShippingSettings();
        if (settings && isMounted) {
          setShippingSettings(settings);
        }
      } catch (err) {
        console.warn("Shipping settings fetch error:", err);
      }
    };
    loadShipping();

    const handleStorageChange = () => {
      if (isMounted) {
        setShippingSettings(getCachedShippingSettings());
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      isMounted = false;
      controller.abort();
      clearTimeout(timeoutId);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // =====================================================
  // BANNER IMAGE & DESCRIPTION FALLBACKS
  // =====================================================

  const bannerImage =
    banner?.image_url || DEFAULT_BANNER.image_url;

  const bannerDescription =
    banner?.description || DEFAULT_BANNER.description;

  return (
    <div style={styles.heroWrapper}>

      {/* =================================================
          ANNOUNCEMENT
      ================================================= */}

      <div style={styles.announcementBar}>
        ✦ FREE SHIPPING ABOVE ₹{formattedThreshold} ✦ USE CODE VANYA10 ✦
        LUXURY HANDLOOMS ✦
      </div>

      {/* =================================================
          ROYAL LOADING DOTS OVERLAY
      ================================================= */}
      {isLoadingBanner && (
        <div style={styles.loadingDotsOverlay} className="hero-loading-overlay">
          <div style={styles.loadingDotsCard}>
            <span style={styles.loadingTag}>✦ VANYA COLLECTIONS ✦</span>
            <div style={styles.dotsWrapper}>
              <span className="hero-dot hero-dot-1"></span>
              <span className="hero-dot hero-dot-2"></span>
              <span className="hero-dot hero-dot-3"></span>
            </div>
            <span style={styles.loadingText}>Unveiling Handloom Splendor...</span>
          </div>
        </div>
      )}

      {/* =================================================
          DYNAMIC HERO IMAGE
      ================================================= */}

      <div
        style={{
          ...styles.imageLayer,
          backgroundImage: `url("${bannerImage}")`,
        }}
      />

      {/* =================================================
          SHINE EFFECT
      ================================================= */}

      <div style={styles.shineLayer}></div>

      {/* =================================================
          LIGHT OVERLAY
      ================================================= */}

      <div style={styles.overlay}></div>

      {/* =================================================
          CONTENT
      ================================================= */}

      <div style={styles.content}>

        {/* =================================================
            LOGO
        ================================================= */}

        <div style={styles.logoContainer}>
          <div style={styles.logoMain}>
            Vanya collections
          </div>

          <div style={styles.logoSub}>
            Budget friendly house
          </div>
        </div>

        {/* =================================================
            HERO TEXT
        ================================================= */}

        <div style={styles.heroText}>

          <p style={styles.topLabel}>
            2023 · INDIA'S FINEST
          </p>

          <h1 style={styles.mainHeading}>
            Elegance Woven
            <br />
            in Every Thread
          </h1>

          <p style={styles.description}>
            {bannerDescription}
          </p>

          {/* =================================================
              BUTTONS
          ================================================= */}

          <div style={styles.buttonContainer}>

            <Link
              to="/collection"
              style={styles.linkStyle}
            >
              <button 
                style={styles.primaryBtn}
                className="hero-primary-btn"
              >
                ✦ Explore Collections
              </button>
            </Link>

            <Link
              to="/shop"
              style={styles.linkStyle}
            >
              <button 
                style={styles.secondaryBtn}
                className="hero-secondary-btn"
              >
                Shop All Sarees →
              </button>
            </Link>

          </div>

        </div>

        {/* =================================================
            STATS
        ================================================= */}

        <div style={styles.stats}>

          <div style={styles.statItem}>
            <h3>10,000+</h3>
            <span>Happy Clients</span>
          </div>

          <div style={styles.statItem}>
            <h3>500+</h3>
            <span>Exclusive Designs</span>
          </div>

          <div style={styles.statItem}>
            <h3>100%</h3>
            <span>Authentic Weaves</span>
          </div>

        </div>

      </div>

      {/* =================================================
          SCROLLING TICKER
      ================================================= */}

      <div style={styles.tickerBanner}>

        <div style={styles.tickerTrack}>

          {[...tickerItems, ...tickerItems].map(
            (item, index) => (
              <span
                key={index}
                style={styles.tickerItem}
              >
                ✦ {item}
              </span>
            )
          )}

        </div>

      </div>

      {/* =================================================
          CSS ANIMATIONS
      ================================================= */}

      <style>
        {`

          * {
            box-sizing: border-box;
          }


          /* ==========================================
             BUTTON SHIMMER
          ========================================== */

          @keyframes shimmer {

            0% {
              background-position: -200% center;
            }

            100% {
              background-position: 200% center;
            }

          }


          /* ==========================================
             IMAGE SHINE
          ========================================== */

          @keyframes imageShine {

            0% {
              transform:
                translateX(-120%)
                skewX(-18deg);

              opacity: 0;
            }

            15% {
              opacity: 0;
            }

            30% {
              opacity: 0.35;
            }

            55% {
              opacity: 0.15;
            }

            70% {
              transform:
                translateX(120%)
                skewX(-18deg);

              opacity: 0;
            }

            100% {
              transform:
                translateX(120%)
                skewX(-18deg);

              opacity: 0;
            }

          }


          /* ==========================================
             TEXT FADE
          ========================================== */

          @keyframes fadeUp {

            from {
              opacity: 0;
              transform: translateY(40px);
            }

            to {
              opacity: 1;
              transform: translateY(0);
            }

          }


          /* ==========================================
             TICKER
          ========================================== */

          @keyframes scroll {

            0% {
              transform: translateX(0);
            }

            100% {
              transform: translateX(-50%);
            }

          }


          /* ==========================================
             MOBILE
          ========================================== */

          .hero-primary-btn {
            transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1) !important;
          }
          .hero-primary-btn:hover {
            transform: translateY(-3px) scale(1.02);
            box-shadow: 0 14px 35px rgba(201, 155, 39, 0.6), 0 0 20px rgba(251, 238, 164, 0.45) !important;
          }
          .hero-primary-btn:active {
            transform: translateY(-1px) scale(0.98);
          }
          .hero-secondary-btn {
            transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1) !important;
          }
          .hero-secondary-btn:hover {
            transform: translateY(-3px) scale(1.02);
            background: rgba(244, 196, 48, 0.16) !important;
            border-color: #fceea7 !important;
            box-shadow: 0 14px 35px rgba(42, 18, 54, 0.4), 0 0 18px rgba(244, 196, 48, 0.3) !important;
          }
          .hero-secondary-btn:active {
            transform: translateY(-1px) scale(0.98);
          }

          /* ==========================================
             ROYAL LOADING DOTS
          ========================================== */

          .hero-dot {
            width: 13px;
            height: 13px;
            border-radius: 50%;
            background: linear-gradient(135deg, #fceea7 0%, #e2bc53 50%, #c99b27 100%);
            display: inline-block;
            box-shadow: 0 0 12px rgba(226, 188, 83, 0.6);
            animation: heroDotPulse 1.4s infinite ease-in-out both;
          }

          .hero-dot-1 {
            animation-delay: -0.32s;
          }

          .hero-dot-2 {
            animation-delay: -0.16s;
          }

          .hero-dot-3 {
            animation-delay: 0s;
          }

          @keyframes heroDotPulse {
            0%, 80%, 100% {
              transform: scale(0.65);
              opacity: 0.35;
            }
            40% {
              transform: scale(1.25);
              opacity: 1;
              box-shadow: 0 0 18px rgba(252, 238, 167, 0.95);
            }
          }

          @media (max-width: 768px) {

            .hero-heading {
              font-size: 2.5rem !important;
            }

            .hero-stats {
              gap: 20px !important;
              flex-wrap: wrap;
            }

          }

        `}
      </style>

    </div>
  );
};


// =====================================================
// STYLES
// =====================================================

const styles = {

  // ===================================================
  // HERO WRAPPER
  // ===================================================

  heroWrapper: {
    position: "relative",
    minHeight: "calc(100vh - 70px)",

    fontFamily:
      "'Playfair Display', serif",

    backgroundColor: "#2a1236",

    color: "#fff",

    overflow: "hidden",

    display: "flex",
    flexDirection: "column",
  },


  // ===================================================
  // HERO IMAGE
  // ===================================================

  imageLayer: {

    position: "absolute",

    inset: 0,

    backgroundSize: "cover",

    backgroundPosition:
      "center center",

    backgroundRepeat:
      "no-repeat",

    // IMPORTANT:
    // No blur
    filter: "none",

    // Keep image fully visible
    opacity: 1,

    zIndex: 0,
  },


  // ===================================================
  // IMAGE SHINE
  // ===================================================

  shineLayer: {

    position: "absolute",

    top: 0,

    left: "-50%",

    width: "45%",

    height: "100%",

    background:
      "linear-gradient(90deg, transparent, rgba(255,255,255,0.32), rgba(255,235,150,0.22), transparent)",

    transform:
      "skewX(-18deg)",

    willChange: "transform",
    backfaceVisibility: "hidden",

    animation:
      "imageShine 7s ease-in-out infinite",

    pointerEvents: "none",

    zIndex: 1,
  },


  // ===================================================
  // OVERLAY
  // ===================================================

  overlay: {

    position: "absolute",

    inset: 0,

    background:
      "linear-gradient(to bottom, rgba(42,18,54,0.30), rgba(42,18,54,0.52))",

    zIndex: 2,

    pointerEvents: "none",
  },


  // ===================================================
  // ANNOUNCEMENT
  // ===================================================

  announcementBar: {

    textAlign: "center",

    padding: "12px 0",

    fontSize: "11px",

    letterSpacing: "3px",

    background: "#2a1236",

    color: "#f4c430",

    zIndex: 5,

    position: "relative",
  },


  // ===================================================
  // CONTENT
  // ===================================================

  content: {

    position: "relative",

    zIndex: 4,

    padding: "0 8%",

    display: "flex",

    flexDirection: "column",

    justifyContent: "space-between",

    flex: 1,
  },


  // ===================================================
  // LOGO
  // ===================================================

  logoContainer: {

    paddingTop: "40px",
  },


  logoMain: {

    fontSize: "20px",

    letterSpacing: "6px",

    fontWeight: "500",

    background:
      "linear-gradient(90deg,#f4c430,#fff4b0,#f4c430)",

    WebkitBackgroundClip: "text",

    WebkitTextFillColor:
      "transparent",

    textShadow:
      "0 0 18px rgba(244,196,48,0.25)",
  },


  logoSub: {

    fontFamily:
      "'Montserrat', sans-serif",

    fontSize: "10px",

    letterSpacing: "3px",

    marginTop: "5px",

    color:
      "rgba(255,255,255,0.85)",
  },


  // ===================================================
  // HERO TEXT
  // ===================================================

  heroText: {

    maxWidth: "850px",

    animation:
      "fadeUp 1.2s ease forwards",
  },


  topLabel: {

    fontFamily:
      "'Montserrat', sans-serif",

    fontSize: "14px",

    letterSpacing: "8px",

    color: "#f4c430",

    marginBottom: "25px",

    fontWeight: "500",

    textShadow:
      "0 0 15px rgba(244,196,48,0.35)",
  },


  mainHeading: {

    fontSize:
      "clamp(2.5rem, 6.5vw, 5.2rem)",

    lineHeight: "1.08",

    fontWeight: "400",

    color: "#fceea7",

    marginBottom: "18px",

    textShadow:
      "0 2px 15px rgba(0,0,0,0.35), 0 0 25px rgba(252,238,167,0.15)",
  },


  // ===================================================
  // DYNAMIC DESCRIPTION
  // ===================================================

  description: {

    fontFamily:
      "'Montserrat', sans-serif",

    fontSize: "1.05rem",

    lineHeight: "1.75",

    maxWidth: "600px",

    color:
      "rgba(255,255,255,0.92)",

    marginBottom: "32px",

    textShadow:
      "0 2px 8px rgba(0,0,0,0.4)",
  },


  // ===================================================
  // BUTTON CONTAINER
  // ===================================================

  buttonContainer: {

    display: "flex",

    gap: "18px",

    flexWrap: "wrap",

    marginBottom: "20px",
  },


  linkStyle: {

    textDecoration: "none",
  },


  // ===================================================
  // PRIMARY BUTTON
  // ===================================================

  primaryBtn: {

    padding: "16px 36px",

    borderRadius: "999px",

    border: "none",

    fontWeight: "700",

    fontSize: "13.5px",

    letterSpacing: "1.2px",

    textTransform: "uppercase",

    fontFamily: "'Montserrat', sans-serif",

    cursor: "pointer",

    background:
      "linear-gradient(135deg, #fceea7 0%, #e2bc53 50%, #c99b27 100%)",

    color: "#2a1236",

    boxShadow:
      "0 8px 25px rgba(226, 188, 83, 0.4), 0 2px 6px rgba(0,0,0,0.2)",

    display: "inline-flex",

    alignItems: "center",

    justifyContent: "center",

    gap: "10px",
  },


  // ===================================================
  // SECONDARY BUTTON
  // ===================================================

  secondaryBtn: {

    padding: "16px 36px",

    borderRadius: "999px",

    border:
      "1.5px solid rgba(244, 196, 48, 0.85)",

    backdropFilter: "blur(10px)",

    WebkitBackdropFilter: "blur(10px)",

    background:
      "rgba(42, 18, 54, 0.45)",

    color: "#fceea7",

    fontWeight: "700",

    fontSize: "13.5px",

    letterSpacing: "1.2px",

    textTransform: "uppercase",

    fontFamily: "'Montserrat', sans-serif",

    cursor: "pointer",

    boxShadow:
      "0 8px 25px rgba(0, 0, 0, 0.25)",

    display: "inline-flex",

    alignItems: "center",

    justifyContent: "center",

    gap: "10px",
  },


  // ===================================================
  // STATS
  // ===================================================

  stats: {

    display: "flex",

    gap: "60px",

    paddingBottom: "40px",

    fontFamily:
      "'Montserrat', sans-serif",

    zIndex: 4,
  },


  statItem: {

    display: "flex",

    flexDirection: "column",
  },


  // ===================================================
  // TICKER
  // ===================================================

  tickerBanner: {

    position: "relative",

    zIndex: 10,

    background: "#f4c430",

    padding: "15px 0",

    width: "100%",

    overflow: "hidden",

    borderTop:
      "1px solid rgba(0,0,0,0.1)",
  },


  tickerTrack: {

    display: "flex",

    width: "max-content",

    animation:
      "scroll 30s linear infinite",
  },


  tickerItem: {

    fontFamily:
      "'Montserrat', sans-serif",

    fontSize: "13px",

    fontWeight: "700",

    color: "#2a1236",

    letterSpacing: "2px",

    padding: "0 40px",

    whiteSpace: "nowrap",
  },

  // ===================================================
  // LOADING DOTS OVERLAY
  // ===================================================

  loadingDotsOverlay: {
    position: "absolute",
    top: "35px",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 15,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(26, 10, 33, 0.88)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    transition: "opacity 0.4s ease, visibility 0.4s ease",
  },

  loadingDotsCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 45px",
    borderRadius: "20px",
    backgroundColor: "rgba(42, 18, 54, 0.8)",
    border: "1.5px solid rgba(226, 188, 83, 0.35)",
    boxShadow: "0 15px 35px rgba(0, 0, 0, 0.5), 0 0 25px rgba(226, 188, 83, 0.15)",
    textAlign: "center",
    maxWidth: "90%",
  },

  loadingTag: {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "11px",
    letterSpacing: "4px",
    color: "#e2bc53",
    fontWeight: "700",
    marginBottom: "18px",
    textShadow: "0 0 10px rgba(226, 188, 83, 0.5)",
  },

  dotsWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    margin: "8px 0 16px",
  },

  loadingText: {
    fontFamily: "'Playfair Display', serif",
    fontStyle: "italic",
    fontSize: "15px",
    color: "rgba(253, 248, 242, 0.85)",
    letterSpacing: "0.5px",
  },
};

export default HeroSection;